import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { axiosInstance } from '../../../axiosApi';
import { AnalyticsEvents, trackEvent } from '../../utils/analytics';
import {
  canOpenBillingPortal,
  checkoutReturnUrls,
  FinanceInvoice,
  FinancePayment,
  formatBillingDate,
  formatMoneyCents,
  formatTokenCount,
  humanizeStatus,
  pickPrimaryInvoice,
  SubscriptionMe,
} from '../../utils/finance';

const GlassCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  backdrop-filter: blur(10px);
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 1rem;
  padding: 2rem;
  width: 100%;
  max-width: 1000px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
`;

const CardTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
  padding-bottom: 1rem;
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};

  &:last-child {
    border-bottom: none;
  }
`;

const SettingLabel = styled.span`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
  opacity: 0.75;
`;

const SettingValue = styled.span`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
  text-align: right;
`;

const BodyText = styled.p`
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.75;
  margin: 0 0 1rem 0;
  line-height: 1.5;
`;

const ErrorText = styled.p`
  color: #ff6b6b;
  margin: 0.75rem 0 0 0;
  font-size: 0.95rem;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const StyledButton = styled.button`
  background: ${({ theme }) => theme.main};
  border: none;
  border-radius: 0.5rem;
  color: #fff;
  padding: 0.8rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${({ theme }) => theme.main}66;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SecondaryButton = styled(StyledButton)`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  color: ${({ theme }) => theme.colors.text};

  &:hover {
    box-shadow: none;
    border-color: ${({ theme }) => theme.main};
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.7;
  font-weight: 600;
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
  color: ${({ theme }) => theme.colors.text};
`;

const InvoiceLink = styled.a`
  color: ${({ theme }) => theme.main};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

function apiErrorMessage(error: unknown, fallback: string): string {
  const axiosError = error as {
    response?: { data?: { detail?: string } };
    message?: string;
  };
  return axiosError.response?.data?.detail || axiosError.message || fallback;
}

const BillingSection = (): JSX.Element => {
  const [invoices, setInvoices] = useState<FinanceInvoice[]>([]);
  const [payments, setPayments] = useState<FinancePayment[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [actionError, setActionError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const loadBilling = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const [invoiceResponse, paymentResponse, subscriptionResponse] = await Promise.all([
        axiosInstance.get<FinanceInvoice[]>('/finance/invoices/'),
        axiosInstance.get<FinancePayment[]>('/finance/payments/'),
        axiosInstance.get<SubscriptionMe>('/finance/subscription/'),
      ]);
      setInvoices(Array.isArray(invoiceResponse.data) ? invoiceResponse.data : []);
      setPayments(Array.isArray(paymentResponse.data) ? paymentResponse.data : []);
      setSubscription(subscriptionResponse.data || null);
    } catch (error: unknown) {
      setInvoices([]);
      setPayments([]);
      setSubscription(null);
      setListError(apiErrorMessage(error, 'Could not load billing information.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  const primaryInvoice = useMemo(() => pickPrimaryInvoice(invoices), [invoices]);
  const hasPortalAccess = useMemo(() => canOpenBillingPortal(invoices), [invoices]);

  const historyRows = useMemo(() => {
    if (invoices.length) {
      return invoices.map((invoice) => ({
        key: `invoice-${invoice.id}`,
        date: invoice.created,
        description: invoice.description || 'Invoice',
        amount: formatMoneyCents(
          invoice.amount_paid || invoice.amount_due,
          invoice.currency
        ),
        status: humanizeStatus(invoice.status),
        url: invoice.hosted_invoice_url || '',
      }));
    }
    return payments.map((payment) => ({
      key: `payment-${payment.id}`,
      date: payment.paid_at || payment.created,
      description: 'Payment',
      amount: formatMoneyCents(payment.amount, payment.currency),
      status: humanizeStatus(payment.status),
      url: '',
    }));
  }, [invoices, payments]);

  const handleManageBilling = async () => {
    setActionError('');
    setPortalLoading(true);
    try {
      const returnUrl = `${window.location.origin}/account/`;
      const response = await axiosInstance.post<{ portal_url: string }>(
        '/finance/portal/',
        { return_url: returnUrl }
      );
      const portalUrl = response.data?.portal_url;
      if (!portalUrl) {
        setActionError('Billing portal could not be opened. Try again.');
        return;
      }
      trackEvent(AnalyticsEvents.BILLING_PORTAL_OPENED);
      window.location.assign(portalUrl);
    } catch (error: unknown) {
      setActionError(
        apiErrorMessage(error, 'Could not open the billing portal. Try again.')
      );
    } finally {
      setPortalLoading(false);
    }
  };

  const handleStartCheckout = async () => {
    setActionError('');
    setCheckoutLoading(true);
    try {
      const { success_url, cancel_url } = checkoutReturnUrls();
      trackEvent(AnalyticsEvents.CHECKOUT_STARTED, { source: 'account_billing' });
      const response = await axiosInstance.post<{ checkout_url: string }>(
        '/finance/checkout/',
        { success_url, cancel_url }
      );
      const checkoutUrl = response.data?.checkout_url;
      if (!checkoutUrl) {
        setActionError('Checkout could not be started. Try again.');
        return;
      }
      window.location.assign(checkoutUrl);
    } catch (error: unknown) {
      setActionError(apiErrorMessage(error, 'Could not start checkout. Try again.'));
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <>
      <GlassCard data-testid="billing-section">
        <CardTitle>Billing</CardTitle>
        {loading ? (
          <BodyText>Loading billing information…</BodyText>
        ) : listError ? (
          <ErrorText role="alert">{listError}</ErrorText>
        ) : subscription?.plan || primaryInvoice ? (
          <>
            <SettingRow>
              <SettingLabel>Plan</SettingLabel>
              <SettingValue>
                {subscription?.plan?.name ||
                  primaryInvoice?.description ||
                  'Chat Subscription'}
              </SettingValue>
            </SettingRow>
            <SettingRow>
              <SettingLabel>Status</SettingLabel>
              <SettingValue>
                {humanizeStatus(subscription?.status || primaryInvoice?.status || 'none')}
              </SettingValue>
            </SettingRow>
            {subscription?.plan ? (
              <SettingRow>
                <SettingLabel>Price</SettingLabel>
                <SettingValue>
                  {formatMoneyCents(
                    subscription.plan.price_cents,
                    subscription.plan.currency
                  )}
                  {subscription.plan.interval ? ` / ${subscription.plan.interval}` : ''}
                </SettingValue>
              </SettingRow>
            ) : primaryInvoice ? (
              <SettingRow>
                <SettingLabel>Amount</SettingLabel>
                <SettingValue>
                  {formatMoneyCents(
                    primaryInvoice.amount_paid || primaryInvoice.amount_due,
                    primaryInvoice.currency
                  )}
                  {primaryInvoice.period_end ? ' / period' : ''}
                </SettingValue>
              </SettingRow>
            ) : null}
            {subscription?.usage ? (
              <>
                <SettingRow>
                  <SettingLabel>Prompts remaining</SettingLabel>
                  <SettingValue>
                    {subscription.usage.prompts_remaining === null
                      ? '—'
                      : `${subscription.usage.prompts_remaining} / ${subscription.usage.prompt_quota} (${subscription.usage.window_hours}h)`}
                  </SettingValue>
                </SettingRow>
                <SettingRow>
                  <SettingLabel>Tokens this period</SettingLabel>
                  <SettingValue>
                    in {formatTokenCount(subscription.usage.tokens_in_period)} · out{' '}
                    {formatTokenCount(subscription.usage.tokens_out_period)}
                  </SettingValue>
                </SettingRow>
              </>
            ) : null}
            {primaryInvoice ? (
              <SettingRow>
                <SettingLabel>Period end</SettingLabel>
                <SettingValue>
                  {formatBillingDate(primaryInvoice.period_end)}
                </SettingValue>
              </SettingRow>
            ) : null}
          </>
        ) : (
          <BodyText>
            No active subscription yet. Complete payment to unlock billing management
            and invoice history.
          </BodyText>
        )}

        {!loading && !listError && (
          <ButtonRow>
            {hasPortalAccess ? (
              <StyledButton
                type="button"
                onClick={handleManageBilling}
                disabled={portalLoading}
              >
                {portalLoading ? 'Opening…' : 'Manage subscription'}
              </StyledButton>
            ) : subscription?.needs_checkout === false ? (
              <BodyText style={{ margin: 0 }}>
                Complimentary access — no payment required.
              </BodyText>
            ) : (
              <StyledButton
                type="button"
                onClick={handleStartCheckout}
                disabled={checkoutLoading}
              >
                {checkoutLoading ? 'Starting…' : 'Complete payment'}
              </StyledButton>
            )}
            <SecondaryButton type="button" onClick={loadBilling} disabled={loading}>
              Refresh
            </SecondaryButton>
          </ButtonRow>
        )}
        {actionError ? <ErrorText role="alert">{actionError}</ErrorText> : null}
      </GlassCard>

      <GlassCard data-testid="billing-history">
        <CardTitle>Billing history</CardTitle>
        {loading ? (
          <BodyText>Loading history…</BodyText>
        ) : listError ? (
          <ErrorText role="alert">{listError}</ErrorText>
        ) : historyRows.length === 0 ? (
          <BodyText>No invoices or payments yet.</BodyText>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <StyledTable>
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Description</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  <Th>Invoice</Th>
                </tr>
              </thead>
              <tbody>
                {historyRows.map((row) => (
                  <tr key={row.key}>
                    <Td>{formatBillingDate(row.date)}</Td>
                    <Td>{row.description}</Td>
                    <Td>{row.amount}</Td>
                    <Td>{row.status}</Td>
                    <Td>
                      {row.url ? (
                        <InvoiceLink href={row.url} target="_blank" rel="noreferrer">
                          View
                        </InvoiceLink>
                      ) : (
                        '—'
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </div>
        )}
      </GlassCard>
    </>
  );
};

export default BillingSection;
